import { useMatch } from 'react-router-dom';
import { POLICY_GATE_TAILS } from './policy-gate.ts';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, LayoutDashboard } from 'lucide-react';
import {
  usePrimaryNavItems,
  useCaseNavItems,
  usePracticeNavItems,
  useProfileNavItems,
  type PrimaryNavItem,
} from './primary-nav.tsx';
import { useMe } from './use-me.ts';
import { useCasePracticeId, useCaseScopedCapability } from './active-practice.tsx';
import { usePersona } from './use-persona.ts';
import { useTokenStore } from '../api/use-api.ts';
import { useCaseRead } from './use-case-read.ts';
import { getPortalOverview } from '../api/portal.ts';

export interface NavBackLink {
  to: string;
  label: string;
  testId: string;
  Icon: typeof ArrowLeft;
}

export interface NavContext {
  /** Items for the current scope, already permission-filtered. */
  items: PrimaryNavItem[];
  /** Context escape hatches rendered above the items (empty for global scope). */
  backLinks: NavBackLink[];
  /** Help, pulled out so surfaces can pin it at the bottom. Null when not offered. */
  helpItem: PrimaryNavItem | null;
  /** `items` minus Help - what a surface should render in its main list. */
  itemsToRender: PrimaryNavItem[];
  /**
   * The heading above a context nav, when the context is a PERSON rather than a screen.
   *
   * The client's rail names the service recipient — "Sam Okafor", or "My care" when the
   * client IS the recipient. Deliberately not a role word: "client" is the PRACTICE's term
   * for the relationship and belongs in the staff register, the permission matrix and the
   * ROPA. A parent opening the portal is not thinking "I am a client"; they are thinking
   * about a person (owner decision 2026-08-19).
   */
  contextTitle: string | null;
}

/**
 * The navigation model for the current route - the single source of truth shared
 * by the desktop left rail (AppSidebar) and the mobile drawer (MobileNavDrawer).
 *
 * Extracted from AppSidebar (v7.100) because the rail was the ONLY renderer of
 * the four context navs, and it is `hidden md:flex`: below 768px the case (up to
 * 13 items), practice, portal-case and profile navs did not exist in the DOM at
 * all, collapsing mobile navigation to hub-and-spoke. Both surfaces now derive
 * from this hook so a new context nav cannot reach one and miss the other.
 * Enforced by tests/architecture/mobile-nav-completeness.test.ts.
 */
export function useNavContext(): NavContext {
  // useMatch operates on the current URL; useParams would not carry :caseId here
  // because these surfaces live in ProtectedLayout (parent route, no :caseId).
  // Two patterns needed per scope: one for sub-routes (/*) and one for the root.
  const caseMatchWild = useMatch('/cases/:caseId/*');
  const caseMatchExact = useMatch('/cases/:caseId');
  const caseId = (caseMatchWild ?? caseMatchExact)?.params.caseId ?? null;

  // Practice context: exclude /practices/new (creation form) and the two policy gates,
  // /practices/:id/dpa and /practices/:id/terms, which deliberately render without chrome.
  const practiceMatchWild = useMatch('/practices/:practiceId/*');
  const practiceMatchExact = useMatch('/practices/:practiceId');
  const rawPracticeId = (practiceMatchWild ?? practiceMatchExact)?.params.practiceId;
  const tail = practiceMatchWild?.params['*'];
  const isPolicyGateRoute = tail !== undefined && (POLICY_GATE_TAILS as readonly string[]).includes(tail);
  const practiceId =
    rawPracticeId !== 'new' && !isPolicyGateRoute ? (rawPracticeId ?? null) : null;

  const profileMatchWild = useMatch('/profile/*');
  const profileMatchExact = useMatch('/profile');
  const isProfileContext = (profileMatchWild ?? profileMatchExact) != null;

  const me = useMe();
  const store = useTokenStore();

  const memberships = me.data?.memberships ?? [];
  const practiceRoleSet = new Set(memberships.map((m) => m.roleInPractice));

  // Skips the audited business-view read for a member who can never hold billing
  // authority anywhere — `/me` already answers that without a request.
  const { canManageBillingAnywhere, isClientOnly } = usePersona();
  // Read the case REACTIVELY (same ['case', caseId] key as the case screens, so React
  // Query dedupes - one fetch, shared cache). A previous non-reactive qc.getQueryData
  // read meant that on a direct load/reload the nav rendered before the case query
  // resolved and then never re-rendered, so owner-gated (Agreement/Billing) and
  // clinical-gated nav items silently never appeared for the owner.
  // The business view's answer populates the cache useCaseScopedCapability reads below.
  const { caseQ: caseQuery } = useCaseRead(caseId ?? null, { businessFallback: canManageBillingAnywhere });
  const cachedCase = caseQuery.data?.case ?? null;
  // ONE resolver: the header asks it too, and the two disagreed at the §8.2 actor.
  const casePracticeId = useCasePracticeId();
  // Owner-only nav (case Agreement/Billing, practice Settings) requires a real
  // owner membership. A platform_admin is NOT an owner: on a case they are at
  // most a read-only break-glass `observer` (invariant 13), and they have no
  // /practices access at all. Do not add `|| platform_admin` here.
  const isOwner = caseId
    // not-authorization: persona/nav SHELL (allowed case 3).
    ? memberships.some((m) => m.practiceId === casePracticeId && m.roleInPractice === 'owner')
    // not-authorization: `isOwner` is passed ONLY to useCaseNavItems, which returns []
    // when there is no caseId — so this branch never reaches a rendered item. Kept as
    // the honest default rather than deleted, since the ternary needs both arms.
    : practiceRoleSet.has('owner');

  // The server's rule is BOTH axes (onCase AND practice owner/practitioner/support_worker/
  // client_contact, or step-up) — `case.read_clinical` in myCapabilities is that answer
  // already resolved; the case-role-only read let an admin/observer seated as support_worker
  // see clinical nav items the server refuses.
  const isClinicalMember = (cachedCase?.myCapabilities ?? []).includes('case.read_clinical');

  // The nav reads the server's capability set for THIS practice, not the role — and the
  // whole membership, because some surfaces are gated on more than capabilities alone.
  const practiceMembership = practiceId
    ? (memberships.find((m) => m.practiceId === practiceId) ?? null)
    : null;

  // One flag drove both links until 2026-08-17, and they are two questions: credentials are
  // user-scoped, while `cpd.*` is checked against the practice the CPD screen acts on - so an
  // admin in the active practice was offered a log every `cpd.*` route refuses.
  const { hasOwnRecordMembership, canHoldWorkingAgreement } = usePersona();
  // ANY membership, not the chosen one: the CPD screen picks its own practice now.
  const canLogCpdHere = hasOwnRecordMembership;

  const globalItems = usePrimaryNavItems();
  // The Billing nav item's own rule — see useCaseNavItems. Off-case fallback shared with
  // CaseBusinessOverview's `canBill` via `useCaseScopedCapability`; called unconditionally
  // here, not short-circuited into the `||` below, per Rules of Hooks.
  const businessAuthorityCanBill = useCaseScopedCapability('case.manage_billing');
  // Same off-case fallback, same reason: a bare on-case actor without membership
  // authority can still be refused `case.read_members`.
  const membershipAuthorityCanReadMembers = useCaseScopedCapability('case.read_members');
  const canReadPayers =
    (cachedCase?.myCapabilities ?? []).includes('case.read_payers') || businessAuthorityCanBill;
  const canReadAudit = (cachedCase?.myCapabilities ?? []).includes('case.read_audit');
  const canReadIncidents = (cachedCase?.myCapabilities ?? []).includes('case.read_incidents');
  const canReadMembers =
    (cachedCase?.myCapabilities ?? []).includes('case.read_members') || membershipAuthorityCanReadMembers;
  // Same off-case fallback: CaseAgreementScreen reads the business view's contract (TF-0037).
  const contractAuthorityReadsContract = useCaseScopedCapability('case.read_contract');
  const canReadContract =
    (cachedCase?.myCapabilities ?? []).includes('case.read_contract') || contractAuthorityReadsContract;
  const caseItems = useCaseNavItems(
    caseId ?? '', isOwner, isClinicalMember, canReadPayers, canReadAudit, canReadIncidents,
    canReadMembers, cachedCase !== null, canReadContract,
  );
  const practiceItems = usePracticeNavItems(practiceId ?? '', practiceMembership);
  // The client's own naming of the case ("My care" vs the recipient's name), which the case
  // read does not carry. Same query key as the client desk, so React Query dedupes to one
  // fetch and the rail re-renders when it resolves rather than reading a cache once.
  // not-authorization: whether to fetch the client's own NAMING of the case. Gates a
  // label, never an affordance.
  const isClientOnCase = cachedCase?.myCaseRole === 'client_contact';
  const clientOverviewQuery = useQuery({
    queryKey: ['portal-overview'],
    queryFn: () => getPortalOverview(store),
    enabled: isClientOnCase,
  });
  const clientCase = clientOverviewQuery.data?.find((c) => c.caseId === caseId) ?? null;
  const profileItems = useProfileNavItems(hasOwnRecordMembership, canLogCpdHere, canHoldWorkingAgreement);

  const dashboardBack = (testId: string): NavBackLink => ({
    to: '/',
    label: 'Dashboard',
    testId,
    Icon: LayoutDashboard,
  });

  let items = globalItems;
  let backLinks: NavBackLink[] = [];

  if (caseId) {
    items = caseItems;
    backLinks = [
      dashboardBack('nav-dashboard-case'),
      casePracticeId !== null && cachedCase === null
        ? { to: `/practices/${casePracticeId}`, label: 'The practice', testId: 'case-nav-back', Icon: ArrowLeft }
        // "Caseload" is the staff register; the client persona's nav item and heading say "My cases".
        : { to: '/cases', label: isClientOnly ? 'My cases' : 'My caseload', testId: 'case-nav-back', Icon: ArrowLeft },
    ];
  } else if (practiceId) {
    items = practiceItems;
    backLinks = [dashboardBack('practice-nav-back')];
  } else if (isProfileContext) {
    items = profileItems;
    backLinks = [dashboardBack('profile-nav-back')];
  }

  // Help comes from the GLOBAL items, never from `items` — pinned at the bottom in every
  // scope. Deriving it from the context items would silently drop it from all of them.
  const helpItem = globalItems.find((i) => i.testId === 'nav-help') ?? null;

  return {
    items,
    backLinks,
    helpItem,
    contextTitle: clientCase !== null
      ? (clientCase.isSelf ? 'My care' : clientCase.recipientName)
      : null,
    itemsToRender: items.filter((i) => i.testId !== 'nav-help'),
  };
}
